/* ────────────────────────────────────────────────────────────────────────────
 * packages/shared/src/api-types.ts
 *
 * The Cropmate Contract Wages API contract. Imported by apps/api and apps/web.
 * Spec: docs/spec/12-api-contract.md
 *
 * RULES
 *  1. No monetary or quantity value is ever a `number`. Money, Quantity and
 *     Factor are decimal STRINGS. Parse with decimal.js; never with parseFloat.
 *  2. `number` appears only for counts, minutes, pagination and versions.
 *  3. Business dates are 'YYYY-MM-DD'. Timestamps are ISO-8601 UTC with 'Z'.
 *  4. Effective-dated windows are half-open: [validFrom, validTo). `validTo` is
 *     EXCLUSIVE; null means open-ended. `validToInclusive` is display-only.
 * ──────────────────────────────────────────────────────────────────────────── */
export const MONEY_PATTERN = /^-?\d{1,10}(\.\d{1,4})?$/;
export const QUANTITY_PATTERN = /^-?\d{1,11}(\.\d{1,3})?$/;
export const FACTOR_PATTERN = /^-?\d{1,3}(\.\d{1,6})?$/;
export const API_BASE_PATH = '/api/v1';
/**
 * The seven roles of PRD §3.1 that the SEED installs with `is_system = true`.
 *
 * For the seed and for tests ONLY. Runtime authorization never consults this
 * list: it resolves `role → role_permission → permission` out of the database,
 * so a custom role authorises the moment it exists.
 */
export const SYSTEM_ROLE_CODES = [
    'CLERK',
    'SUPERVISOR',
    'HR',
    'PAYROLL',
    'FINANCE',
    'ADMIN',
    'AUDITOR',
];
/**
 * The role → permission seed of §3.4, for the SEVEN SYSTEM ROLES ONLY.
 *
 * ⚠ This is the SEED's source of truth and nothing else. It must NOT be
 * consulted for runtime authorization anywhere: an Admin may edit a system
 * role's permissions after deployment (PRD §10), at which point this table is
 * merely the value the row was born with. `UsersService.permissionsForRoles()`
 * reads the database, and the JWT carries what the database said.
 *
 * NOTE the deliberate gaps in ADMIN: it holds neither `payroll.lock`,
 * `disbursement.write` nor `export.generate`. PRD §3.2 leaves those cells blank,
 * and honouring that is what keeps the payroll separation of duty enforceable.
 * (The rate separation of duty was retired with FR-3.5 — see ADR-0002.)
 */
export const SYSTEM_ROLE_PERMISSIONS = {
    CLERK: [
        'job.read',
        'job.write',
        'worker.read',
        'team.read',
        'rate.read',
        'casual.read',
        'casual.write',
        'config.read',
    ],
    SUPERVISOR: [
        'job.read',
        'job.write',
        'roster.confirm',
        'worker.read',
        'document.read',
        'team.read',
        'rate.read',
        'report.read',
        'config.read',
    ],
    HR: [
        'worker.read',
        'worker.write',
        'document.read',
        'document.write',
        'document.file.read',
        'team.read',
        'team.write',
        'agent.read',
        'agent.write',
        'casual.read',
        'paycomponent.read',
        'paycomponent.write',
        'report.read',
        'config.read',
    ],
    PAYROLL: [
        'job.read',
        'worker.read',
        'document.read',
        'team.read',
        'agent.read',
        'rate.read',
        'rate.write',
        'worktype.write',
        'casual.read',
        'casual.write',
        'paycomponent.read',
        'paycomponent.write',
        'period.manage',
        'payroll.read',
        'payroll.run',
        'disbursement.read',
        'report.read',
        'baseline.upload',
        'config.read',
    ],
    FINANCE: [
        'job.read',
        'worker.read',
        'document.read',
        'team.read',
        'agent.read',
        'rate.read',
        'rate.write',
        'paycomponent.read',
        'payroll.read',
        'payroll.lock',
        'disbursement.read',
        'disbursement.write',
        'export.generate',
        'report.read',
        'audit.read',
        'casual.read',
        'config.read',
    ],
    ADMIN: [
        'job.read',
        'job.write',
        'roster.confirm',
        'worker.read',
        'worker.write',
        'document.read',
        'document.write',
        'document.file.read',
        'team.read',
        'team.write',
        'agent.read',
        'agent.write',
        'rate.read',
        'rate.write',
        'worktype.write',
        'casual.read',
        'casual.write',
        'paycomponent.read',
        'paycomponent.write',
        'period.manage',
        'payroll.read',
        'payroll.run',
        'payroll.reopen',
        'disbursement.read',
        'report.read',
        'baseline.upload',
        'config.read',
        'config.write',
        'user.read',
        'user.manage',
        'audit.read',
        'company.manage',
    ],
    AUDITOR: [
        'job.read',
        'worker.read',
        'document.read',
        'team.read',
        'agent.read',
        'rate.read',
        'casual.read',
        'paycomponent.read',
        'payroll.read',
        'disbursement.read',
        'report.read',
        'config.read',
        'user.read',
        'audit.read',
    ],
};
/** Real router paths from 13-ui-screens.md §18. `_app` is a PATHLESS layout, so the
 *  file route `_app.sheets.tsx` is served at the URL `/sheets`. Emit the public path.
 *
 *  SEED ONLY — migration 0012 backfills `role.landing_route` from this map and the
 *  column is the authority from then on, because a custom role has a landing route
 *  too and no compile-time map can know it. */
export const ROLE_LANDING_ROUTE = {
    CLERK: '/sheets',
    SUPERVISOR: '/sheets',
    HR: '/workers',
    PAYROLL: '/payroll',
    FINANCE: '/payroll',
    ADMIN: '/admin/users',
    AUDITOR: '/payroll', // AUDITOR holds payroll.read but not job.write; /payroll is the useful landing
};
/** ADR-0017. SCREAMING_SNAKE_CASE, the shape the seven enum members had (`unit_of_measure_code_chk`). */
export const UNIT_CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;
export const UNIT_CODE_MAX_LENGTH = 24;
export const UNIT_NAME_MAX_LENGTH = 60;
export const UNIT_SHORT_LABEL_MAX_LENGTH = 24;
export const TIME_CATEGORIES = [
    'NORMAL',
    'OVERTIME',
    'HOLIDAY',
    'HOLIDAY_OVERTIME',
    'REST_DAY',
    'REST_DAY_OVERTIME',
];
export const PAYSLIP_LETTERHEAD_MODES = ['WITH', 'WITHOUT'];
/* ─────────────────────── public holidays (F24a–F24d) ─────────────────────── */
/**
 * FEDERAL is gazetted nationally; STATE is gazetted by one state. Telok Gong is in
 * Selangor, so a state holiday is as real to the plant as a federal one — the scope
 * exists so the clerk can see WHICH, not so the engine branches on it.
 *
 * A `VARCHAR + CHECK` column, not a PG enum (`10-data-model.md` §5).
 */
export const HOLIDAY_SCOPES = ['FEDERAL', 'STATE'];
/**
 * The two NON-team choices of the payroll run's team filters (Register, Adjustments; client,
 * 21 Oct 2026: casual workers are on no team, "so how should we filter it"). Sent inside
 * `teamIds` beside team uuids, and the set is a UNION: `M1,CASUAL` is M1's workers plus every
 * casual. `CASUAL` = a row whose pay scheme is casual (`payScheme` or `paySchemesPaid`), on a
 * team or not; `NO_TEAM` = a row with no team. Only the payroll routes accept them — the job
 * reports' `teamIds` stay uuids.
 */
export const TEAM_FILTER_CASUAL = 'CASUAL';
export const TEAM_FILTER_NO_TEAM = 'NO_TEAM';
//# sourceMappingURL=api-types.js.map